import React from 'react';
import { ClientAccessSettingsProps } from '../../../../ClientAccess.types';
import { Toggle } from '../../../../../CommonComponents/OPL/Toggle/Toggle';
import { DashboardFeatureId } from '../../../../../AdviserArea/AdviserArea.types';
import { beFiPlanSubFeatures } from '../Dashboard/Dashboard.meta';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Tooltip } from '../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { useBeFi } from './useBeFi';

interface Props extends ClientAccessSettingsProps {
  accessSet: DashboardFeatureId[];
  hasDashboardAccess: boolean;
  dashboardAccessOptions: { label: string; value: number }[];
  disabledDashboardAccessOptions: DashboardFeatureId[];
  toggleSubFeature: (checked: boolean, id: DashboardFeatureId) => void;
}

export const Befi: React.FC<Props> = ({
  accessSet,
  hasDashboardAccess,
  dashboardAccessOptions,
  disabledDashboardAccessOptions,
  hiddenBeFiTools,
  toggleSubFeature,
  onChange,
}) => {
  const { hasFinancialPlanEnabled, allowedBeFiTools, allowedBeFiIds } = useBeFi({
    accessSet,
    dashboardAccessOptions,
    hiddenBeFiTools,
  });

  if (!allowedBeFiTools.length) {
    return null;
  }

  const allChecked = [...allowedBeFiIds].some(val => accessSet?.includes(val));

  return (
    <>
      <Tooltip
        content="When Financial Planning is enabled, BeFi tools are available by default—unless they've been disabled at the firm level. You can manage this client's BeFi tools in Client Settings."
        placement="bottom-start"
        offset={[0, -4]}
      >
        <Box position="relative">
          <Toggle
            variant="setting"
            checked={hasFinancialPlanEnabled || allChecked}
            disabled={!hasDashboardAccess || hasFinancialPlanEnabled}
            onChange={evt => {
              if (hasDashboardAccess) {
                onChange({
                  dashboardAccess: evt.target.checked
                    ? [...accessSet, ...allowedBeFiIds.filter(val => !accessSet.includes(val))]
                    : accessSet.filter(val => !allowedBeFiIds.includes(val)),
                });
              }
            }}
            my="16"
          >
            Behavioral Finance
          </Toggle>

          <Icon
            className="far fa-info-circle"
            color="neutral.400"
            fontSize="body"
            ml={132}
            mt={-37}
            css="position: absolute"
          />
        </Box>
      </Tooltip>

      {allowedBeFiTools.map(({ label: featureName, value: featureId }) => {
        return (
          <>
            {featureName !== 'PulseCheck' ? (
              <Toggle
                key={featureId}
                variant="setting"
                checked={hasFinancialPlanEnabled || accessSet.includes(featureId)}
                disabled={
                  !hasDashboardAccess ||
                  hasFinancialPlanEnabled ||
                  disabledDashboardAccessOptions.includes(featureId)
                }
                onChange={evt => toggleSubFeature(evt.target.checked, featureId)}
                my="16"
                ml="24"
              >
                {featureName}
              </Toggle>
            ) : (
              <Tooltip
                content="Goal setting in PulseCheck requires financial planning to be enabled."
                placement="bottom-start"
                offset={[0, -4]}
              >
                <Box position="relative">
                  <Toggle
                    key={featureId}
                    variant="setting"
                    checked={hasFinancialPlanEnabled || accessSet.includes(featureId)}
                    disabled={
                      !hasDashboardAccess ||
                      hasFinancialPlanEnabled ||
                      disabledDashboardAccessOptions.includes(featureId)
                    }
                    onChange={evt => toggleSubFeature(evt.target.checked, featureId)}
                    my="16"
                    ml="24"
                  >
                    {featureName}
                  </Toggle>
                  <Icon
                    className="far fa-info-circle"
                    color="neutral.400"
                    fontSize="body"
                    ml={108}
                    mt={-37}
                    css="position: absolute"
                  />
                </Box>
              </Tooltip>
            )}
          </>
        );
      })}
    </>
  );
};
